import React, {useEffect, useState} from 'react';
import Calendar from './availability-slot-picker/calendar';
import TimeSlotGrid from './availability-slot-picker/time-slot-grid';
import classNames from "classnames";

export const generateTimeSlots = (workHours, sessionLength) => {
    if (!workHours) return [];

    const [start, end] = workHours.split('-');
    const startTime = parseTime(start);
    const endTime = parseTime(end);
    const slots = [];

    let current = new Date(startTime);
    while (current < endTime) {
        slots.push(formatTime(current));
        current.setMinutes(current.getMinutes() + sessionLength);
    }

    return slots;
};

const parseTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
};

const formatTime = (date) => {
    return date.toTimeString().slice(0, 5);
};

const AvailabilitySlotPicker = ({
                                    timeSlots,
                                    sessionLength = 30,
                                    onSelect,
                                }) => {
    const today = new Date();
    const [selectedDate, setSelectedDate] = useState(null);
    const [bookedSlots, setBookedSlots] = useState([]);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const loadSlots = async () => {
            if (!timeSlots || !selectedDate) return;
            setLoading(true);
            try {
                const dateStr = selectedDate.toISOString().split('T')[0];
                const booked = []; // placeholder for real booked times
                setBookedSlots(booked);

                const dayOfWeek = selectedDate.getDay();
                const workHours = getWorkHours(timeSlots, dayOfWeek);
                const slots = generateTimeSlots(workHours, sessionLength);
                setAvailableSlots(slots);
            } finally {
                setLoading(false);
            }
        };

        loadSlots();
    }, [timeSlots, selectedDate, sessionLength]);

    const getWorkHours = (loc, day) => {
        switch (day) {
            case 0:
                return loc.sundayWorkHours;
            case 6:
                return loc.saturdayWorkHours;
            default:
                return loc.weekdayWorkHours;
        }
    };

    const handleTimeClick = (time) => {
        if (!selectedDate) return;
        const dateStr = selectedDate.toISOString().split('T')[0];
        onSelect(`${dateStr}T${time}`);
    };

    return (
        <div
            className={classNames(
                "transition-all duration-700 ease-in-out bg-white rounded-xl shadow-xl p-6 mx-auto"
            )}>
            <h2 className="text-2xl font-semibold mb-6 text-center text-gray-800">Book an Appointment</h2>

            {!timeSlots ? (
                <div className="text-center py-16 text-gray-400 animate-pulse">Loading data...</div>
            ) : (
                <div className="relative flex flex-col sm:flex-row gap-6">
                    {/* Calendar Panel */}
                    <div className="sm:w-3/5 w-full z-10 shrink-0">
                        <Calendar
                            selectedDate={selectedDate}
                            onDateSelect={setSelectedDate}
                            timeSlots={timeSlots}
                        />
                    </div>

                    {/* Time Slots Slide-In Panel */}
                    <div
                        className={classNames(
                            "sm:w-2/5 w-full absolute sm:static",
                        )}>
                        <div className={classNames(
                            "transition-all absolute duration-700 ease-in-out transform text-sm font-normal text-gray-500",
                            !selectedDate
                                ? 'opacity-100 translate-x-0 sm:relative'
                                : 'opacity-0 pointer-events-none absolute'
                        )}>
                            Please select a date to view available times.
                        </div>
                        <div className={classNames(
                            "transition-all absolute duration-700 ease-in-out transform",
                            selectedDate
                                ? 'opacity-100 translate-x-0 sm:relative'
                                : 'opacity-0 pointer-events-none absolute'
                        )}>
                            <h4 className="text-base font-medium text-gray-700 mb-3">
                                {selectedDate && (
                                    <div
                                        className="text-sm font-normal text-gray-500">{selectedDate.toDateString()}</div>
                                )}
                            </h4>

                            {loading ? (
                                <div className="text-sm text-gray-400 animate-pulse">Loading available times...</div>
                            ) : (
                                <TimeSlotGrid
                                    slots={availableSlots}
                                    bookedSlots={bookedSlots}
                                    onSelect={handleTimeClick}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AvailabilitySlotPicker;